import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TaskStatus } from '@nexus/core';
import { updateTask, type Task } from '@nexus/workspace';
import { useStore } from '../../db/context';
import { cn } from '../../lib/cn';
import { addDays } from '@nexus/core';
import { byDueThenPriority, todayIso } from './format';
import { TaskRow } from './TaskRow';

const COLUMNS: TaskStatus[] = ['todo', 'doing', 'waiting', 'done'];
/** Done column shows recent work only, to keep the board light. */
const DONE_DAYS = 14;

/** Kanban by status; drag a card to another column to change its status. */
export function TaskBoard({
  tasks,
  projectTitle,
}: {
  tasks: Task[];
  projectTitle: (id: string | null) => string | null;
}) {
  const { t } = useTranslation();
  const store = useStore();
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<TaskStatus | null>(null);
  const since = addDays(todayIso(), -DONE_DAYS);

  const byId = new Map(tasks.map((x) => [x.id, x]));
  const drop = (status: TaskStatus) => {
    const task = dragging ? byId.get(dragging) : undefined;
    setDragging(null);
    setOver(null);
    if (task && task.status !== status) void updateTask(store, task, { status });
  };

  return (
    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
      {COLUMNS.map((status) => {
        const list = tasks
          .filter((x) => x.status === status)
          .filter((x) => status !== 'done' || !x.doneAt || x.doneAt >= since)
          .sort(byDueThenPriority);
        return (
          <section
            key={status}
            aria-label={t(`tasks.statuses.${status}`)}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(status);
            }}
            onDragLeave={() => setOver((o) => (o === status ? null : o))}
            onDrop={(e) => {
              e.preventDefault();
              drop(status);
            }}
            className={cn(
              'flex min-h-40 flex-col rounded-lg bg-bg-subtle p-2',
              over === status && 'ring-2 ring-accent',
            )}
          >
            <h2 className="mb-2 flex items-center gap-2 px-1 text-sm font-semibold">
              {t(`tasks.statuses.${status}`)}
              <span className="rounded-full bg-bg px-1.5 text-xs font-normal text-fg-muted">
                {list.length}
              </span>
            </h2>
            <div className="flex flex-col gap-1.5">
              {list.map((x) => (
                <div
                  key={x.id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', x.id);
                    setDragging(x.id);
                  }}
                  onDragEnd={() => setDragging(null)}
                  className={cn(
                    'cursor-grab rounded-lg border border-border bg-bg',
                    dragging === x.id && 'opacity-50',
                  )}
                  data-testid="board-card"
                >
                  <TaskRow task={x} projectTitle={projectTitle(x.projectId)} compact />
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
